import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Save, Loader2, Sparkles, ImageIcon, Images } from 'lucide-react';
import { HeroStatsEditor } from './HeroStatsEditor';
import { Card } from '@/components/ui/card';
import { uploadHeroImageToEvent } from '@/utils/uploadHeroImage';
import { useEventImages } from '@/contexts/EventImagesContext';
import type { EventType } from '@/utils/eventUtils';

interface HeroContentEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventType: EventType;
  category: string;
  currentData: {
    title?: string;
    subtitle?: string;
    description?: string;
    location?: string;
    startDate?: string;
    endDate?: string;
    primaryCTA?: string;
    secondaryCTA?: string;
    primaryCTALink?: string;
    secondaryCTALink?: string;
    stats?: Array<{ value: string; label: string }>;
  };
  onSuccess?: () => void;
}

export function HeroContentEditor({
  open,
  onOpenChange,
  eventId,
  eventType,
  category,
  currentData,
  onSuccess,
}: HeroContentEditorProps) {
  // Helper function to format date for input[type="date"]
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const [title, setTitle] = useState(currentData.title || '');
  const [description, setDescription] = useState(currentData.description || '');
  const [location, setLocation] = useState(currentData.location || '');
  const [startDate, setStartDate] = useState(formatDateForInput(currentData.startDate));
  const [endDate, setEndDate] = useState(formatDateForInput(currentData.endDate));
  const [primaryCTA, setPrimaryCTA] = useState(currentData.primaryCTA || 'Get Your Tickets Now');
  const [secondaryCTA, setSecondaryCTA] = useState(currentData.secondaryCTA || 'Learn More');
  const [primaryCTALink, setPrimaryCTALink] = useState(currentData.primaryCTALink || '');
  const [secondaryCTALink, setSecondaryCTALink] = useState(currentData.secondaryCTALink || '');
  const [isSaving, setIsSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [isSettingHero, setIsSettingHero] = useState(false);
  const { toast } = useToast();
  const { images, loading: imagesLoading } = useEventImages();

  useEffect(() => {
    if (open) {
      setTitle(currentData.title || '');
      setDescription(currentData.description || '');
      setLocation(currentData.location || '');
      setStartDate(formatDateForInput(currentData.startDate));
      setEndDate(formatDateForInput(currentData.endDate));
      setPrimaryCTA(currentData.primaryCTA || 'Get Your Tickets Now');
      setSecondaryCTA(currentData.secondaryCTA || 'Learn More');
      setPrimaryCTALink(currentData.primaryCTALink || '');
      setSecondaryCTALink(currentData.secondaryCTALink || '');
    }
  }, [open, currentData]);

  const getTableName = () => {
    const tableMap: Record<string, string> = {
      'festivals': 'festivals',
      'bootcamps': 'bootcamps',
      'academies': 'academies',
      'workations': 'workations',
      'retreats': 'retreats',
    };
    return tableMap[category] || 'festivals';
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const tableName = getTableName();
      const updateData: any = {
        name: title,
        description,
        location,
        start_date: startDate,
        end_date: endDate,
        primary_cta_link: primaryCTALink,
        secondary_cta_link: secondaryCTALink,
      };

      const { error } = await supabase
        .from(tableName as any)
        .update(updateData)
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Hero content updated successfully"
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving hero content:', error);
      toast({
        title: "Error",
        description: "Failed to save hero content",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateHeroImage = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Prompt required",
        description: "Please describe the hero image you want to generate",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      // Generate image using AI
      const { data, error } = await supabase.functions.invoke('generate-block-image', {
        body: { 
          prompt: `Professional hero banner image: ${aiPrompt}. High quality, wide aspect ratio 16:9, ${location || 'beautiful location'}, vibrant and engaging` 
        },
      });

      if (error) throw error;

      if (data.success && data.imageUrl) {
        // Upload to event as hero image
        const uploadResult = await uploadHeroImageToEvent(
          data.imageUrl,
          eventId,
          eventType,
          'hero'
        );

        setGeneratedImageUrl(uploadResult.publicUrl);
        
        toast({
          title: "✨ Hero Image Generated",
          description: "AI-generated hero image has been uploaded successfully",
        });
        
        setAiPrompt('');
        
        // Trigger image refresh
        localStorage.setItem('hero-image-update', Date.now().toString());
        window.dispatchEvent(new Event('storage'));
      } else {
        throw new Error(data.error || 'Failed to generate image');
      }
    } catch (error: any) {
      console.error('Hero image generation error:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate hero image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectExistingImage = async (imageUrl: string) => {
    setIsSettingHero(true);
    try {
      const uploadResult = await uploadHeroImageToEvent(
        imageUrl,
        eventId,
        eventType,
        'hero'
      );

      toast({
        title: "Hero Image Set",
        description: "Selected image has been set as hero image",
      });

      // Trigger image refresh
      localStorage.setItem('hero-image-update', Date.now().toString());
      window.dispatchEvent(new Event('storage'));
    } catch (error: any) {
      console.error('Error setting hero image:', error);
      toast({
        title: "Error",
        description: "Failed to set hero image",
        variant: "destructive",
      });
    } finally {
      setIsSettingHero(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Hero Section</DialogTitle>
          <DialogDescription>
            Update the hero section content, dates, location, and statistics
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Event Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter event title"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter event description"
              rows={4}
            />
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Enter event location"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryCTA">Primary Button Text</Label>
              <Input
                id="primaryCTA"
                value={primaryCTA}
                onChange={(e) => setPrimaryCTA(e.target.value)}
                placeholder="Get Your Tickets Now"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondaryCTA">Secondary Button Text</Label>
              <Input
                id="secondaryCTA"
                value={secondaryCTA}
                onChange={(e) => setSecondaryCTA(e.target.value)}
                placeholder="Learn More"
              />
            </div>
          </div>

          {/* CTA Button Links */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryCTALink">Primary Button Link</Label>
              <Input
                id="primaryCTALink"
                type="url"
                value={primaryCTALink}
                onChange={(e) => setPrimaryCTALink(e.target.value)}
                placeholder="https://example.com/tickets"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondaryCTALink">Secondary Button Link</Label>
              <Input
                id="secondaryCTALink"
                type="url"
                value={secondaryCTALink}
                onChange={(e) => setSecondaryCTALink(e.target.value)}
                placeholder="https://example.com/info"
              />
            </div>
          </div>

          {/* Hero Image Management */}
          <div className="pt-4 border-t">
            <Tabs defaultValue="generate" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="generate">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate New
                </TabsTrigger>
                <TabsTrigger value="library">
                  <Images className="h-4 w-4 mr-2" />
                  Image Library
                </TabsTrigger>
              </TabsList>

              <TabsContent value="generate" className="mt-4">
                <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border-2">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">AI Hero Image Generator</h3>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      Generate a custom hero image for this event using AI (free during beta)
                    </p>

                    {generatedImageUrl && (
                      <div className="relative rounded-lg overflow-hidden border-2 border-primary/20">
                        <img 
                          src={generatedImageUrl} 
                          alt="Generated hero preview" 
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute top-2 right-2 bg-primary/90 text-primary-foreground px-2 py-1 rounded text-xs">
                          ✓ Uploaded
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Describe Your Hero Image</Label>
                      <Textarea
                        placeholder="e.g., Swiss Alps mountain peaks at sunset with modern tech elements, futuristic digital nomad workspace overlooking mountains, vibrant festival atmosphere in alpine setting"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <Button 
                      onClick={handleGenerateHeroImage} 
                      disabled={isGenerating || !aiPrompt.trim()}
                      className="w-full"
                      variant="default"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating & Uploading...
                        </>
                      ) : (
                        <>
                          <ImageIcon className="h-4 w-4 mr-2" />
                          Generate Hero Image
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-muted-foreground">
                      💡 Tip: Include event theme, location ({location || 'location'}), mood, and style preferences for best results
                    </p>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="library" className="mt-4">
                <Card className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Images className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">Select from Existing Images</h3>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      Choose from previously uploaded event photos and AI-generated images
                    </p>

                    {imagesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : images.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No images available yet</p>
                        <p className="text-sm">Upload images or generate with AI first</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                        {images.map((image) => (
                          <button
                            key={image.id}
                            onClick={() => handleSelectExistingImage(image.publicUrl || image.file_path)}
                            disabled={isSettingHero}
                            className="relative group rounded-lg overflow-hidden border-2 border-border hover:border-primary transition-colors aspect-video"
                          >
                            <img
                              src={image.publicUrl || image.file_path}
                              alt={image.alt_text || image.file_name || 'Event image'}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="text-white text-sm font-medium">
                                Set as Hero
                              </span>
                            </div>
                            {image.category === 'hero' && (
                              <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs">
                                Current Hero
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Stats Editor */}
          <div className="pt-4 border-t">
            <HeroStatsEditor
              eventId={eventId}
              eventType={eventType}
              currentStats={currentData.stats}
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
